import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { useDispatch } from "react-redux";
import { useSigninWithCode } from "../utils/authHelpers";
import { inTabletBrowser } from "../utils/authHelpers/useSSOAutoLogin";
import { useScriptConverter } from "../utils/languageHelpers";
import { resolveLoginRedirect } from "../utils/loginRedirect";
import { SET_WARNING, CLOSE_WARNING } from "../actions/type";
import {
  default as ACCESS_LEVELS,
  ADMINS,
  STUDENT
} from "../config/accessLevels";

const MyBestUserLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const signinWithCode = useSigninWithCode();
  const dispatch = useDispatch();
  const converter = useScriptConverter();

  useEffect(() => {
    const search = qs.parse(location.search, { ignoreQueryPrefix: true });

    signinWithCode(search.code)
      .then(({ data }) => {
        const roles = data.signinWithCode.me.roles || [];

        // If user has no roles, show warning and redirect to home
        if (roles.length === 0) {
          dispatch({
            type: SET_WARNING,
            payload: {
              alert:
                "身分認證錯誤!\n請洽詢您的校長索取登入邀請碼,或是聯繫教務及工程師\n\nIdentity error!\nPlease check invitation code with your principal or Melody consultants.",
              confirm: () => {
                dispatch({ type: CLOSE_WARNING, payload: {} });
                navigate("/");
              }
            }
          });
          return;
        }

        // Check localStorage first (written by the tablet logout button)
        let previousUrl = localStorage.getItem("tabletReturnPath");
        const fromLogout = !!previousUrl;
        if (previousUrl) {
          // Clear it after reading
          localStorage.removeItem("tabletReturnPath");
        }

        // Fallback to sessionStorage if no tabletReturnPath
        if (!previousUrl) {
          previousUrl = sessionStorage.getItem("previousURL");

          if (previousUrl) {
            sessionStorage.removeItem("previousURL");
          }
        }

        // Last resort: the durable copy Authorization keeps for tablet
        // visitors, for when the sign-in came back in a fresh tab whose
        // sessionStorage is empty. A direct link, not a sign-out page, so
        // fromLogout stays false and the role segment is rewritten. Consumed
        // on every sign-in, read or not, so it cannot outlive the visit that
        // wrote it.
        const tabletPreviousUrl = localStorage.getItem("tabletPreviousURL");
        localStorage.removeItem("tabletPreviousURL");
        if (!previousUrl && tabletPreviousUrl) {
          previousUrl = tabletPreviousUrl;
        }

        // Parse isTablet from multiple sources in the URL
        // 1. Query parameter: ?isTablet=true
        const isTabletFromQuery = search.isTablet === "true";

        // 2. Check if previousUrl contains /isTablet=true or /isTablet (path segment)
        const isTabletFromPreviousUrl =
          previousUrl &&
          (previousUrl.includes("/isTablet=true") ||
            previousUrl.includes("/isTablet"));

        // 3. Check localStorage for isTablet flag
        const isTabletFromLocalStorage =
          localStorage.getItem("isTablet") === "true";

        // Combined check - isTablet is true if found in ANY location
        const isTabletParam =
          isTabletFromQuery ||
          isTabletFromPreviousUrl ||
          isTabletFromLocalStorage;

        // Persist isTablet to localStorage if detected from URL or previousUrl
        if (isTabletParam && !isTabletFromLocalStorage) {
          localStorage.setItem("isTablet", "true");
        }

        // Determine the user's role-based route
        const userRoleRoute = roles.some((role) =>
          ACCESS_LEVELS[ADMINS].includes(role)
        )
          ? "admin"
          : roles.some((role) => ACCESS_LEVELS[STUDENT].includes(role))
            ? "student"
            : "teacher";

        // Determine redirect target based on previousUrl or default to role-based home
        // (see utils/loginRedirect.js for the logout vs direct-link rules).
        // HealthCheckGuard will handle showing toast for restricted routes in tablet mode
        const redirectTarget = resolveLoginRedirect({
          previousUrl,
          fromLogout,
          userRoleRoute
        });

        // Check if we're in an iframe
        const isInIframe = window.self !== window.top;

        const diagnosticData = {
          isTabletFromQuery,
          isTabletFromPreviousUrl,
          isTabletFromLocalStorage,
          isTabletParam,
          roles,
          previousUrl,
          userRoleRoute,
          redirectTarget,
          isInIframe
        };

        // CRITICAL: If we're in an iframe, send diagnostic data to parent window
        // and let parent handle the navigation
        if (isInIframe) {
          window.top.postMessage(
            {
              type: "MYBESTUSER_LOGIN_DIAGNOSTIC",
              data: diagnosticData
            },
            "*"
          );
        } else {
          // Not in iframe, navigate directly
          navigate(redirectTarget);
        }
      })
      .catch((error) => {
        const backToHome = () => {
          dispatch({ type: CLOSE_WARNING, payload: {} });
          navigate("/");
        };

        // On a tablet the code came from the mybestuser session in this
        // browser, so "/" just asks that same session again and lands back
        // here (e.g. an account whose school contract expired). The only way
        // out is to sign in with another account in tablet-auth-app, whose
        // bridge replaces the session and sends the browser back to us.
        // Only the UA check: shouldTrackSession() needs a session marker
        // that is written after a successful sign-in, so on a freshly
        // imaged tablet whose very first sign-in fails it is still false
        // and the first round would fall back to the single-button path.
        if (inTabletBrowser()) {
          dispatch({
            type: SET_WARNING,
            payload: {
              alert: error.message,
              confirmText: converter({
                en: "Open Tablet Auth",
                tw: "開啟平板認證",
                cn: "打开平板认证"
              }),
              confirm: () => {
                dispatch({ type: CLOSE_WARNING, payload: {} });
                window.top.location.href =
                  "tabletauth://open?redirect=" +
                  encodeURIComponent(window.location.origin + "/");
              },
              cancel: () => navigate("/")
            }
          });
          return;
        }

        dispatch({
          type: SET_WARNING,
          payload: {
            alert: error.message,
            confirm: backToHome
          }
        });
      });
  }, [signinWithCode, navigate, location, dispatch, converter]);

  return null;
};

export default MyBestUserLogin;
